import {
  requestCreateModelMail,
  requestGetModelMail,
  requestUpdateModelMail,
} from "@/state/modelMail/ModelMailEffects";
import { useModelMail } from "@/state/modelMail/ModelMailHook";
import {
  CreateModelMail,
  ModelMail,
  ModelMailTypes,
} from "@/type/core/models/models.types";
import { toastError } from "@/utils/helpers/toast-helper";
import PageLayout from "@components/core/layouts/PageLayout";
import CardSection from "@components/core/section/CardSection";
import SignatureMailForm from "@components/core/subComponents/SignatureMailForm";
import { ROUTES } from "@routes/Routes";
import { formValidateTriggers } from "@utils/Constant";
import { Form } from "antd";
import { useForm } from "antd/es/form/Form";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const MailModelScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { selectedOffice } = useModelMail();
  const officeId = selectedOffice?.id;

  const params = useParams();
  const [searchParams] = useSearchParams();
  const type: ModelMailTypes = (searchParams.get("type") ??
    "invitation") as ModelMailTypes;

  const [signatureInitialValue, setSignatureInitialValue] = useState("");

  const [form] = useForm<CreateModelMail>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [titleLabel, setTitleLabel] = useState<string>(
    selectedOffice?.alias ?? t("offices.mailModel.userNavTitle"),
  );

  const setFormValues = (data: ModelMail) => {
    const formModelMail: CreateModelMail = {
      defaultModel: data.defaultModel ?? false,
      message: data.mailConfiguration?.message ?? "",
      senderEmailLabel: data.mailConfiguration?.senderEmailLabel ?? "",
      senderEmailLabelText: data.mailConfiguration?.senderEmailLabelText ?? "",
      signature: data.mailConfiguration?.signature ?? "",
      subject: data.mailConfiguration?.subject ?? "",
      name: data.name,
    };
    form.setFieldsValue(formModelMail);
    setSignatureInitialValue(formModelMail.signature);
  };

  useEffect(() => {
    if (!officeId) {
      setTitleLabel(t("offices.mailModel.userNavTitle"));
    } else {
      setTitleLabel(selectedOffice && selectedOffice.alias);
    }
  }, [selectedOffice, officeId, t]);

  useEffect(() => {
    if (params.mailModelId) {
      setIsLoading(true);
      void requestGetModelMail({ id: params.mailModelId });
    }
  }, [params]);

  useEffect(() => {
    return requestGetModelMail.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setFormValues(result.data);
      } else {
        toastError(t("error.toast.modelMail.retrieve"));
      }
      setIsLoading(false);
    });
  });

  useEffect(() => {
    return requestCreateModelMail.done.watch(({ result }) => {
      if (result.ok) {
        navigate(ROUTES.extranet.menu.offices.mailModel.list.generate());
      } else {
        toastError(t("error.toast.modelMail.mailsCreate"));
      }
      setIsLoading(false);
    });
  });

  useEffect(() => {
    return requestUpdateModelMail.done.watch(({ result }) => {
      if (result.ok) {
        navigate(ROUTES.extranet.menu.offices.mailModel.list.generate());
      } else {
        toastError(t("error.toast.modelMail.mailsModify"));
      }
      setIsLoading(false);
    });
  });

  const handleSubmit = (values: CreateModelMail) => {
    setIsLoading(true);

    const modelMail: ModelMail = {
      defaultModel: values.defaultModel ?? false,
      mailConfiguration: {
        message: values.message,
        senderEmailLabel: values.senderEmailLabel,
        senderEmailLabelText: values.senderEmailLabelText,
        signature: values.signature,
        subject: values.subject,
        isDefault: values.defaultModel ?? false,
      },
      name: values.name,
      officeId,
      type,
    };

    if (params.mailModelId) {
      void requestUpdateModelMail({ dto: modelMail, id: params.mailModelId });
    } else {
      void requestCreateModelMail({ dto: modelMail });
    }
  };

  return (
    <PageLayout
      container
      returnRoute={ROUTES.extranet.menu.offices.mailModel.list.generate()}
      isLoading={isLoading}
    >
      <CardSection
        title={
          type === "invitation"
            ? t("offices.mailModel.form.invitation.title") + titleLabel
            : t("offices.mailModel.form.endOfTransaction.title") + titleLabel
        }
      >
        <Form
          form={form}
          {...formValidateTriggers}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <SignatureMailForm
            mailType={type ?? "invitation"}
            officeId={officeId}
            isFromModel
            mailContentVisible
            form={form}
            signatureInitialValue={signatureInitialValue}
            setSignatureInitialValue={setSignatureInitialValue}
          />
        </Form>
      </CardSection>
    </PageLayout>
  );
};

export default MailModelScreen;
